import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Text, Flex, Button, IconButton } from "@radix-ui/themes";
import { PlusIcon } from "@radix-ui/react-icons";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { SnippetIcon } from "@/v2/assets/icons/SnippetIcon";
import { CheckmarkBadgeIcon } from "@/v2/assets/icons/CheckmarkBadgeIcon";
import { SidebarIcon } from "@/v2/assets/icons/SidebarIcon";
import { AiFolderIcon } from "@/v2/assets/icons/AiFolderIcon";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import {
  useSnippets,
  useSnippetById,
  useCreateSnippet,
} from "@/v2/entities/KnowledgeSnippet";
import { pathToTree } from "./utils/pathToTree";
import { CollectionsSidebar } from "@/v2/features/knowledge-management/parts/CollectionsSidebar";
import { SnippetDetail } from "@/v2/features/knowledge-management/parts/SnippetDetail";
import { CreateSnippetDialog } from "@/v2/features/knowledge-management/parts/CreateSnippetDialog";
import { CreateCollectionDialog } from "@/v2/features/knowledge-management/parts/CreateCollectionDialog";
import { useSnippetsUrlState } from "./hooks/useKnowledgeManagementUrlState";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import styles from "./KnowledgeManagement.module.scss";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";

const DEFAULT_SIDEBAR_SIZE = 30;
const COLLAPSED_SIDEBAR_SIZE = 6;
const COLLAPSED_SIDEBAR_PIXEL_WIDTH = 56;
const MIN_EXPANDED_SIDEBAR_SIZE = 20;

export function KnowledgeManagement() {
  const { data: snippets, isLoading, isError } = useSnippets();
  const { mutate: createSnippet, isLoading: isCreating } = useCreateSnippet();

  const treeData = useMemo(() => {
    if (!snippets) return [];
    return pathToTree(snippets);
  }, [snippets]);

  const collectionPaths = useMemo(() => {
    if (!snippets || snippets.length === 0) {
      return [""];
    }

    const paths = new Set<string>([""]);

    snippets.forEach((snippet) => {
      const cleanPath = snippet.path.startsWith("/")
        ? snippet.path.slice(1)
        : snippet.path;
      if (!cleanPath) {
        return;
      }
      const parts = cleanPath.split("/");
      let current = "";
      parts.forEach((part) => {
        current = current ? `${current}/${part}` : part;
        paths.add(current);
      });
    });

    return Array.from(paths).sort((a, b) => {
      if (a === b) return 0;
      if (a === "") return -1;
      if (b === "") return 1;
      return a.localeCompare(b);
    });
  }, [snippets]);

  const { initialState, updateUrl } = useSnippetsUrlState({
    snippets,
  });

  const [selectedSnippetId, setSelectedSnippetId] = useState<string | null>(
    null
  );
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [hasHydrated, setHasHydrated] = useState(false);
  const isSyncingFromUrlRef = useRef(false);
  const [createSnippetDialogOpen, setCreateSnippetDialogOpen] = useState(false);
  const [createCollectionDialogOpen, setCreateCollectionDialogOpen] =
    useState(false);
  const [searchPhrase, setSearchPhrase] = useState("");
  const sidebarPanelRef = useRef<ImperativePanelHandle | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [previousSidebarSize, setPreviousSidebarSize] =
    useState<number>(DEFAULT_SIDEBAR_SIZE);

  const initialSnippetId = initialState.selectedSnippetId ?? null;
  const initialSelectedPath = initialState.selectedPath ?? null;
  const initialVersionId = initialState.selectedVersionId ?? null;

  const {
    data: selectedSnippetDetail,
    isLoading: isSnippetLoading,
    isError: isSnippetError,
    refetch: refetchSelectedSnippet,
  } = useSnippetById(selectedSnippetId);

  // Compute the selected node ID for the tree
  // Tree node ID = collection path + snippet title
  const selectedNodeId = useMemo(() => {
    if (!selectedSnippetId || !snippets) return null;
    const snippet = snippets.find((s) => s.id === selectedSnippetId);
    if (!snippet) return null;

    // Remove leading slash from path to match tree node ID format
    const cleanPath = snippet.path.startsWith("/")
      ? snippet.path.slice(1)
      : snippet.path;

    // Combine collection path + title (tree node ID format)
    return cleanPath ? `${cleanPath}/${snippet.title}` : snippet.title;
  }, [selectedSnippetId, snippets]);

  const handleSnippetSelect = useCallback(
    (snippetId: string) => {
      if (!isSidebarOpen && sidebarPanelRef.current) {
        const targetSize = Math.max(
          previousSidebarSize,
          MIN_EXPANDED_SIDEBAR_SIZE
        );
        sidebarPanelRef.current.resize(targetSize);
        setIsSidebarOpen(true);
      }
      setSelectedSnippetId(snippetId);
      const snippet = snippets?.find((s) => s.id === snippetId);
      if (snippet) {
        // snippet.path is already the collection path (without snippet title)
        setSelectedPath(snippet.path || null);
      }
      setSelectedVersionId(null);
    },
    [snippets, isSidebarOpen, previousSidebarSize]
  );

  const handleOpenCreateSnippet = useCallback(() => {
    setCreateSnippetDialogOpen(true);
  }, []);

  const handleSidebarOpenChange = useCallback(
    (open: boolean) => {
      const panel = sidebarPanelRef.current;

      if (panel) {
        if (open) {
          const targetSize = Math.max(
            previousSidebarSize,
            MIN_EXPANDED_SIDEBAR_SIZE
          );
          panel.resize(targetSize);
        } else {
          const currentSize = panel.getSize();
          if (currentSize > COLLAPSED_SIDEBAR_SIZE) {
            setPreviousSidebarSize(
              Math.max(currentSize, MIN_EXPANDED_SIDEBAR_SIZE)
            );
          }
          panel.resize(COLLAPSED_SIDEBAR_SIZE);
        }
      }

      setIsSidebarOpen(open);
    },
    [previousSidebarSize]
  );

  const handleCreateSnippetSubmit = useCallback(
    (
      collectionPath: string,
      snippetName: string,
      isInternal: boolean
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        const normalizedPath = collectionPath.startsWith("/")
          ? collectionPath.slice(1)
          : collectionPath;
        createSnippet(
          {
            path: normalizedPath,
            title: snippetName,
            isInternal,
          },
          {
            onSuccess: (response) => {
              // Navigate to newly created snippet
              setSelectedSnippetId(response.id);
              setSelectedPath(normalizedPath || null);
              // URL auto-updates via existing useEffect
              setSelectedVersionId(null);
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          }
        );
      });
    },
    [createSnippet]
  );

  const handleOpenCreateCollection = useCallback(() => {
    setCreateCollectionDialogOpen(true);
  }, []);

  const handleCreateCollectionSubmit = useCallback(
    (
      parentCollectionPath: string,
      collectionName: string,
      snippetName: string
    ): Promise<void> => {
      return new Promise((resolve, reject) => {
        // Build the collection path
        // No selection: "collectionName"
        // With selection: "selectedPath/collectionName"
        const normalizedParent = parentCollectionPath.startsWith("/")
          ? parentCollectionPath.slice(1)
          : parentCollectionPath;
        const collectionPath = normalizedParent
          ? `${normalizedParent}/${collectionName}`
          : collectionName;

        // HACK: Create a placeholder snippet to create the collection folder
        // The collection will appear in the tree, containing this snippet
        createSnippet(
          {
            path: collectionPath,
            title: snippetName,
            isInternal: false,
          },
          {
            onSuccess: () => {
              // Collection created (as a folder containing the placeholder snippet)
              // React Query auto-refreshes the tree
              setSelectedVersionId(null);
              resolve();
            },
            onError: (error) => {
              reject(error);
            },
          }
        );
      });
    },
    [selectedPath, createSnippet]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedSnippetId(null);
    setSelectedPath(null);
    setSelectedVersionId(null);
  }, []);

  const handleSidebarResize = useCallback(
    (size: number) => {
      if (isSidebarOpen && size > COLLAPSED_SIDEBAR_SIZE) {
        setPreviousSidebarSize(Math.max(size, MIN_EXPANDED_SIDEBAR_SIZE));
      }
    },
    [isSidebarOpen]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const normalizedSnippetId = initialSnippetId;
    const normalizedPath = initialSelectedPath;
    const normalizedVersionId = normalizedSnippetId ? initialVersionId : null;
    const snippetChanged = normalizedSnippetId !== selectedSnippetId;
    const pathChanged = normalizedPath !== selectedPath;
    const versionChanged = normalizedVersionId !== selectedVersionId;

    if (snippetChanged || pathChanged || versionChanged) {
      isSyncingFromUrlRef.current = true;
      setSelectedSnippetId(normalizedSnippetId);
      setSelectedPath(normalizedPath);
      setSelectedVersionId(normalizedVersionId);
    }

    if (!hasHydrated) {
      setHasHydrated(true);
    }
    // Only re-run when URL state (initialSnippetId, initialSelectedPath) changes
    // NOT when local state (selectedSnippetId, selectedPath) changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasHydrated,
    initialSelectedPath,
    initialSnippetId,
    initialVersionId,
    isLoading,
  ]);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    if (isSyncingFromUrlRef.current) {
      isSyncingFromUrlRef.current = false;
      return;
    }

    // Only sync snippet selection to URL, not folder state
    updateUrl({
      selectedSnippetId,
      selectedPath,
      selectedVersionId,
    });
  }, [
    hasHydrated,
    selectedSnippetId,
    selectedPath,
    selectedVersionId,
    updateUrl,
  ]);

  if (isLoading) {
    return (
      <PageLayoutV2>
        <Flex
          className={styles.loadingContainer}
          align="center"
          justify="center"
        >
          <Text size="3" color="gray">
            Loading knowledge base...
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  if (isError) {
    return (
      <PageLayoutV2>
        <Flex
          className={styles.errorContainer}
          direction="column"
          align="center"
          justify="center"
          gap="4"
          p="5"
        >
          <Text size="4" weight="medium">
            Error loading knowledge base
          </Text>
          <Text size="2" color="gray">
            Please try again later
          </Text>
        </Flex>
      </PageLayoutV2>
    );
  }

  return (
    <PageLayoutV2>
      <Flex className={styles.knowledgeManagementContainer}>
        <ResizablePanelGroup
          direction="horizontal"
          className={styles.resizablePanelGroup}
        >
          <ResizablePanel
            ref={sidebarPanelRef}
            defaultSize={DEFAULT_SIDEBAR_SIZE}
            minSize={COLLAPSED_SIDEBAR_SIZE}
            maxSize={60}
            onResize={handleSidebarResize}
            style={
              isSidebarOpen
                ? undefined
                : {
                    flexBasis: `${COLLAPSED_SIDEBAR_PIXEL_WIDTH}px`,
                    minWidth: `${COLLAPSED_SIDEBAR_PIXEL_WIDTH}px`,
                    maxWidth: `${COLLAPSED_SIDEBAR_PIXEL_WIDTH}px`,
                  }
            }
            className={styles.sidebarPanel}
          >
            <Collapsible
              open={isSidebarOpen}
              onOpenChange={handleSidebarOpenChange}
              className={styles.sidebarCollapsible}
            >
              {isSidebarOpen ? (
                <PageHeader
                  title="Snippets"
                  description="Organize and manage your knowledge"
                  icon={
                    <AiFolderIcon
                      size={24}
                      fill={PageColors[ENavigate.KNOWLEDGE_EDITOR] || "gray"}
                    />
                  }
                  actions={
                    <Flex gap="2" align="center">
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="2"
                          aria-label="Hide collections sidebar"
                          aria-pressed={isSidebarOpen}
                        >
                          <SidebarIcon size={14} />
                        </Button>
                      </CollapsibleTrigger>
                      <DropdownMenu
                        items={[
                          {
                            label: "Snippet",
                            onClick: handleOpenCreateSnippet,
                            icon: (
                              <AiFolderIcon size={12} fill="currentColor" />
                            ),
                          },
                          {
                            label: "Collection",
                            onClick: handleOpenCreateCollection,
                            icon: <SnippetIcon size={12} fill="currentColor" />,
                          },
                        ]}
                      >
                        <DropdownMenu.Trigger>
                          <Button
                            variant="solid"
                            color="gray"
                            highContrast
                            disabled={isCreating}
                          >
                            <PlusIcon />
                            New
                          </Button>
                        </DropdownMenu.Trigger>
                      </DropdownMenu>
                    </Flex>
                  }
                />
              ) : (
                <Flex
                  className={styles.sidebarToolbar}
                  align="center"
                  justify="center"
                >
                  <CollapsibleTrigger asChild>
                    <Button
                      variant="ghost"
                      size="2"
                      aria-label="Show collections sidebar"
                      aria-pressed={isSidebarOpen}
                    >
                      <SidebarIcon size={14} />
                    </Button>
                  </CollapsibleTrigger>
                </Flex>
              )}
              <CollapsibleContent className={styles.sidebarContent}>
                <CollectionsSidebar
                  data={treeData}
                  selectedNodeId={selectedNodeId}
                  onSnippetSelect={handleSnippetSelect}
                  onClearSelection={handleClearSelection}
                  collectionPaths={collectionPaths}
                  searchTerm={searchPhrase}
                  onSearchChange={setSearchPhrase}
                />
              </CollapsibleContent>
            </Collapsible>
          </ResizablePanel>

          <ResizableHandle className={styles.resizeHandle} />

          <ResizablePanel
            defaultSize={60}
            minSize={40}
            className={styles.detailPanel}
          >
            <SnippetDetail
              snippetDetail={selectedSnippetDetail}
              isLoading={isSnippetLoading}
              isError={isSnippetError}
              selectedVersionId={selectedVersionId}
              onSelectVersion={setSelectedVersionId}
              onRefreshSnippet={refetchSelectedSnippet}
            />
          </ResizablePanel>
        </ResizablePanelGroup>

        <CreateSnippetDialog
          open={createSnippetDialogOpen}
          onOpenChange={setCreateSnippetDialogOpen}
          defaultCollectionPath={selectedPath ?? ""}
          collectionPaths={collectionPaths}
          onCreateSnippet={handleCreateSnippetSubmit}
        />

        <CreateCollectionDialog
          open={createCollectionDialogOpen}
          onOpenChange={setCreateCollectionDialogOpen}
          defaultParentPath={selectedPath ?? ""}
          collectionPaths={collectionPaths}
          onCreateCollection={handleCreateCollectionSubmit}
        />
      </Flex>
    </PageLayoutV2>
  );
}
